import { Brain } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ChoiceRow } from "./ChoiceRow";

import {
  type ProjectFeedEvent,
  type ProjectMemoryPayload,
} from "../../../services/askPunttProjectFeed.types";
import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../agentComponents/chatAtomRegistry";

export type FeedMemoryCardVariant =
  | "open"
  | "edit"
  | "changeScope"
  | "editedOpen";

export type FeedMemoryScopeOption = {
  id: string;
  label: string;
  description?: string;
};

/**
 * Feed confirmation for a remembered instruction. Variants add Edit / Change
 * Scope / Save using the same strip chrome as the live card — callbacks only;
 * there is no persist API on this surface.
 */
export function FeedMemoryCard({
  event,
  variant = "open",
  onEdit,
  onSave,
  onChangeScope,
  onCancel,
  scopes = [],
  selectedScopeIds = [],
  onToggleScope,
}: {
  event: ProjectFeedEvent;
  variant?: FeedMemoryCardVariant;
  onEdit?: () => void;
  onSave?: (instruction: string) => void;
  onChangeScope?: () => void;
  onCancel?: () => void;
  scopes?: FeedMemoryScopeOption[];
  selectedScopeIds?: string[];
  onToggleScope?: (id: string) => void;
}) {
  const { t } = useTranslation("ui");
  const payload = (event.payload ?? {}) as ProjectMemoryPayload;
  const instruction = payload.instruction ?? event.body ?? "";
  const [draft, setDraft] = useState(instruction);
  const heading =
    variant === "editedOpen"
      ? t("feedMemoryCard.editedTitle")
      : t("feedMemoryCard.savedToProjectMemory");
  const showActions = Boolean(onEdit || onChangeScope);
  const editing = variant === "edit";
  const scoping = variant === "changeScope";

  // Cancelling an edit must discard the typed draft: this card is driven as a
  // single persistent instance whose `variant` is toggled, so a retained draft
  // would surface abandoned edits the next time the user opens Edit.
  const handleCancel = () => {
    setDraft(instruction);
    onCancel?.();
  };

  return (
    <section
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.feedMemoryCard)}
      className="rounded-xl border border-puntt-blue-6 bg-puntt-blue-2 p-3.5"
      data-testid="project-feed-memory-saved"
      data-variant={variant}
      aria-label={heading}
    >
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-puntt-blue-3 text-puntt-accent-9"
        >
          <Brain size={16} weight="fill" />
        </span>
        <span className="text-[13px] font-semibold text-puntt-accent-11">
          {heading}
        </span>
      </div>

      {editing ? (
        <div className="mt-2">
          <label
            htmlFor={`feed-memory-instruction-${event._id}`}
            className="block text-[11px] font-medium text-puntt-neutral-gray-11"
          >
            {t("feedMemoryCard.instructionLabel")}
          </label>
          <textarea
            id={`feed-memory-instruction-${event._id}`}
            data-testid="feed-memory-instruction-input"
            aria-label={t("feedMemoryCard.instructionLabel")}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            className="mt-1 w-full resize-none rounded-md border border-puntt-neutral-gray-6 bg-base-white px-2.5 py-2 text-[13px] text-puntt-neutral-gray-12 outline-none focus:border-puntt-accent-9"
          />
          <div className="mt-2 flex justify-end gap-2">
            {onCancel ? (
              <button
                type="button"
                data-testid="feed-memory-cancel"
                aria-label={t("feedMemoryCard.cancel")}
                onClick={handleCancel}
                className="rounded-md px-2.5 py-1 text-[12px] font-medium text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-3"
              >
                {t("feedMemoryCard.cancel")}
              </button>
            ) : null}
            <button
              type="button"
              data-testid="feed-memory-save"
              aria-label={t("feedMemoryCard.save")}
              disabled={draft.trim().length === 0}
              onClick={() => onSave?.(draft.trim())}
              className="rounded-md bg-puntt-accent-9 px-2.5 py-1 text-[12px] font-medium text-base-white hover:bg-puntt-accent-10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("feedMemoryCard.save")}
            </button>
          </div>
        </div>
      ) : scoping ? (
        <div className="mt-2 flex flex-col gap-2">
          <p className="text-[11px] font-medium text-puntt-neutral-gray-11">
            {t("feedMemoryCard.scopeHeading")}
          </p>
          {scopes.map((scope) => (
            <ChoiceRow
              key={scope.id}
              data-testid={`feed-memory-scope-${scope.id}`}
              label={scope.label}
              description={scope.description}
              selected={selectedScopeIds.includes(scope.id)}
              onClick={() => onToggleScope?.(scope.id)}
            />
          ))}
          <div className="flex justify-end gap-2">
            {onCancel ? (
              <button
                type="button"
                data-testid="feed-memory-cancel"
                aria-label={t("feedMemoryCard.cancel")}
                onClick={handleCancel}
                className="rounded-md px-2.5 py-1 text-[12px] font-medium text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-3"
              >
                {t("feedMemoryCard.cancel")}
              </button>
            ) : null}
            <button
              type="button"
              data-testid="feed-memory-save"
              aria-label={t("feedMemoryCard.save")}
              onClick={() => onSave?.(instruction)}
              className="rounded-md bg-puntt-accent-9 px-2.5 py-1 text-[12px] font-medium text-base-white hover:bg-puntt-accent-10"
            >
              {t("feedMemoryCard.save")}
            </button>
          </div>
        </div>
      ) : (
        <>
          {instruction ? (
            <p
              className="mt-2 text-[12.5px] leading-relaxed text-puntt-neutral-gray-12"
              data-testid="project-feed-memory-instruction"
            >
              {instruction}
            </p>
          ) : null}
          {showActions ? (
            <div className="mt-3 flex gap-2">
              {onEdit ? (
                <button
                  type="button"
                  data-testid="feed-memory-edit"
                  aria-label={t("feedMemoryCard.edit")}
                  onClick={onEdit}
                  className="rounded-md border border-puntt-neutral-gray-6 px-2.5 py-1 text-[12px] font-medium text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-3"
                >
                  {t("feedMemoryCard.edit")}
                </button>
              ) : null}
              {onChangeScope ? (
                <button
                  type="button"
                  data-testid="feed-memory-change-scope"
                  aria-label={t("feedMemoryCard.changeScope")}
                  onClick={onChangeScope}
                  className="rounded-md border border-puntt-neutral-gray-6 px-2.5 py-1 text-[12px] font-medium text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-3"
                >
                  {t("feedMemoryCard.changeScope")}
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
